import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';
import { usePortalUser } from 'vet-bones/utils/portal';

import {
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
  SUPPORTED_DATA_SOURCE_FAMILIES,
} from 'src/constants/dataSourceTypeDescription';
import { SAMPLE_MUSIC_DATA_SOURCE } from 'src/constants/sampleDataSource';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceTypeListSelector } from 'src/selectors/dataSourceAvailableTypeList';
import { dataSourceFamiliesByTypeSelector } from 'src/selectors/dataSourceFamiliesByType';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { isUrlStardogFreeCloudInstance } from 'src/utils/web/isUrlStardogCloudInstance';

/**
 * Hook to get the filtered and sorted list of available data source types.
 * Includes logic for:
 * - Filtering by supported data source families
 * - Alpha sorting by label
 * - Adding sample music data source for free cloud/staff users
 *
 * @returns The sorted list of data source types
 */
export const useDataSourceTypeList = (): string[] => {
  const portalUser = usePortalUser();
  const connection = useReduxSelector(connectionSelector);
  const dataSourceTypeList = useReduxSelector(dataSourceTypeListSelector);
  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );
  const dataSourceFamiliesByType = useReduxSelector(
    dataSourceFamiliesByTypeSelector(connection)
  );
  const isDataSourceDescriptionEndpointSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceDescriptionEndpointSupported',
      connection
    )
  );

  return React.useMemo(() => {
    const list = isDataSourceDescriptionEndpointSupported
      ? Array.from(dataSourceTypeList).filter(
          (dataSourceType) =>
            SUPPORTED_DATA_SOURCE_FAMILIES[
              dataSourceFamiliesByType[dataSourceType]
            ]
        )
      : [];

    list.sort(
      alphaSort((dataSourceType) => dataSourceLabelsByType[dataSourceType])
    );

    // Add sample music data source for free cloud instances or staff users
    if (
      (isUrlStardogFreeCloudInstance(connection?.endpoint || '') ||
        portalUser.data?.is_staff) &&
      list.includes(SAMPLE_MUSIC_DATA_SOURCE.type)
    ) {
      list.push(SAMPLE_MUSIC_DATA_SOURCE_TYPE);
    }

    return list;
  }, [
    isDataSourceDescriptionEndpointSupported,
    dataSourceTypeList,
    dataSourceFamiliesByType,
    dataSourceLabelsByType,
    connection?.endpoint,
    portalUser.data?.is_staff,
  ]);
};
